import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const formatResumeSeconds = (seconds) => {
  const value = Math.max(0, Math.round(Number(seconds) || 0));
  if (value < 60) return `${value}s`;
  const mins = Math.floor(value / 60);
  const secs = value % 60;
  return `${mins}m ${secs}s`;
};

const clampPercent = (value) =>
  Math.max(0, Math.min(100, Number(value) || 0));

const pickText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

export default function ContinuePlaybackSection({
  apiClient,
  prefetchedData = null,
}) {
  const isMobile = window.innerWidth <= 768;
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [storybooks, setStorybooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const normalizeStoryItem = (story) => {
    if (!story) return null;
    if ((story.status || "").toUpperCase() === "FINISHED") return null;

    const id = story.id || story.story_id;
    if (!id) return null;

    const resumeSeconds = Math.max(
      0,
      Math.round(
        Number(
          story.resume_seconds ??
            story.progress_seconds ??
            story.resume_value ??
            story.progress ??
            0,
        ) || 0,
      ),
    );

    const apiProgressPercent = Number(
      story.progress_percent ?? story.progress_percentage ?? story.progressPercent,
    );
    const totalDurationSeconds = Number(
      story.total_duration_seconds ??
        story.duration_seconds ??
        story.video_duration_seconds ??
        story.duration_seconds_total ??
        story.total_duration ??
        story.duration,
    );
    const derivedFromDuration =
      totalDurationSeconds > 0
        ? (resumeSeconds / totalDurationSeconds) * 100
        : Number.NaN;
    const derivedFromRawProgress =
      resumeSeconds > 0 && resumeSeconds <= 100 ? resumeSeconds : Number.NaN;
    const progressPercent = clampPercent(
      Number.isFinite(apiProgressPercent)
        ? apiProgressPercent
        : Number.isFinite(derivedFromDuration)
          ? derivedFromDuration
          : derivedFromRawProgress,
    );
    const visibleProgressPercent =
      progressPercent > 0 ? Math.max(progressPercent, 2) : 0;

    return {
      id,
      type: "story",
      title: pickText(story.title, story.name) || "Untitled Story",
      thumbnail:
        story.thumbnail_url || story.cover_image_url || "/assets/pixley-panda-nobg.png",
      progressLabel:
        story.progress_label ||
        (resumeSeconds > 0
          ? `Resume from ${formatResumeSeconds(resumeSeconds)}`
          : "Continue watching"),
      progressPercent: visibleProgressPercent,
      resumeValue: resumeSeconds,
    };
  };

  const normalizeStorybookItem = (storybook) => {
    if (!storybook) return null;
    if ((storybook.status || "").toUpperCase() === "FINISHED") return null;

    const id = storybook.id || storybook.storybook_id;
    if (!id) return null;

    const resumePage = Math.max(
      0,
      Math.round(
        Number(
          storybook.resume_page ??
            storybook.current_page ??
            storybook.resume_value ??
            storybook.progress ??
            0,
        ) || 0,
      ),
    );

    const apiProgressPercent = Number(
      storybook.progress_percent ??
        storybook.progress_percentage ??
        storybook.progressPercent,
    );
    const totalPages = Number(
      storybook.total_pages ??
        storybook.page_count ??
        storybook.totalPages ??
        storybook.pages_count ??
        storybook.num_pages,
    );
    const derivedFromPages =
      totalPages > 0 ? (resumePage / totalPages) * 100 : Number.NaN;
    const derivedFromRawProgress =
      resumePage > 0 && resumePage <= 100 ? resumePage : Number.NaN;
    const progressPercent = clampPercent(
      Number.isFinite(apiProgressPercent)
        ? apiProgressPercent
        : Number.isFinite(derivedFromPages)
          ? derivedFromPages
          : derivedFromRawProgress,
    );
    const visibleProgressPercent =
      progressPercent > 0 ? Math.max(progressPercent, 2) : 0;

    const resolvedTitle = pickText(storybook.title, storybook.name);

    // TEMP DEBUG: remove once backend payload is consistent.
    if (!resolvedTitle && process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(
        "[ContinuePlayback] Storybook missing both title and name. Using fallback.",
        {
          id,
          storybook,
          keys: Object.keys(storybook || {}),
        },
      );
    }

    return {
      id,
      type: "storybook",
      title: resolvedTitle || "Untitled Storybook",
      thumbnail: storybook.thumbnail_url || "/assets/pixley-panda-nobg.png",
      progressLabel:
        storybook.progress_label ||
        (resumePage > 0
          ? `Resume from page ${resumePage}`
          : "Continue listening"),
      progressPercent: visibleProgressPercent,
      resumeValue: resumePage,
    };
  };

  const items = useMemo(() => {
    const normalizedStories = (stories || []).map(normalizeStoryItem).filter(Boolean);
    const normalizedStorybooks = (storybooks || [])
      .map(normalizeStorybookItem)
      .filter(Boolean);
    return [...normalizedStories, ...normalizedStorybooks];
  }, [stories, storybooks]);

  useEffect(() => {
    if (prefetchedData && typeof prefetchedData === "object") {
      setStories(Array.isArray(prefetchedData.stories) ? prefetchedData.stories : []);
      setStorybooks(
        Array.isArray(prefetchedData.storybooks) ? prefetchedData.storybooks : [],
      );
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      if (!apiClient) {
        if (!cancelled) {
          setStories([]);
          setStorybooks([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const response = await apiClient.getContinueWatching();
        const payload =
          response && typeof response === "object"
            ? response.data && typeof response.data === "object"
              ? response.data
              : response
            : {};
        const nextStories = Array.isArray(payload?.stories) ? payload.stories : [];
        const nextStorybooks = Array.isArray(payload?.storybooks)
          ? payload.storybooks
          : [];

        if (!cancelled) {
          setStories(nextStories);
          setStorybooks(nextStorybooks);
        }
      } catch (error) {
        console.warn("Continue playback fetch failed:", error);
        if (!cancelled) {
          setStories([]);
          setStorybooks([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [apiClient, prefetchedData]);

  if (loading) return null;
  if (stories.length === 0 && storybooks.length === 0) return null;

  return (
    <div
      className="content-row"
      style={{
        marginTop: 0,
        marginBottom: isMobile ? "22px" : "40px",
        position: "relative",
        zIndex: 2,
      }}
    >
      <h2
        className="row-title"
        style={{
          marginBottom: isMobile ? "8px" : "20px",
          marginTop: "0px",
          padding: isMobile ? "0 20px" : "0 40px",
          fontSize: isMobile ? "1.5rem" : undefined,
        }}
      >
        Continue Listening & Watching
      </h2>

      <div
        className="story-grid-container"
        style={{
          // Left inset via padding (aligns with the title); right inset via
          // margin so the scroll viewport ends before the page edge — cards
          // never touch the right edge at rest or end of scroll. Right gutter
          // is intentionally smaller than the left.
          padding: isMobile ? "8px 0 12px 20px" : "20px 0 20px 40px",
          marginRight: isMobile ? "12px" : "16px",
          display: "flex",
          gap: isMobile ? "8px" : "20px",
          overflowX: "auto",
          overflowY: "visible",
          scrollBehavior: "smooth",
          touchAction: "pan-x",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {items.map((item) => (
          <div
            key={`${item.type}:${item.id}`}
            style={{
              flex: "0 0 auto",
              width: isMobile ? "calc(33.333% - 6px)" : "clamp(200px, 23vw, 300px)",
              minWidth: isMobile ? "calc(33.333% - 6px)" : "clamp(200px, 23vw, 300px)",
            }}
          >
            <button
              type="button"
              onClick={() => {
                if (item.type === "story") {
                  navigate(`/stories/${item.id}?resume=${item.resumeValue}`);
                  return;
                }
                navigate(`/audiobooks/${item.id}?resumePage=${item.resumeValue}`);
              }}
              className="show-card story-card"
              style={{
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                padding: 0,
              }}
            >
              <div className="show-poster" style={{ position: "relative" }}>
                <img
                  src={item.thumbnail}
                  alt={
                    item.title ||
                    (item.type === "storybook"
                      ? "Untitled Storybook"
                      : "Untitled Story")
                  }
                  className="poster-image"
                />
              </div>
              <div className="show-info">
                <h3 className="show-title">
                  {item.title ||
                    (item.type === "storybook"
                      ? "Untitled Storybook"
                      : "Untitled Story")}
                </h3>
                <div className="mt-1">
                  <p
                    className="text-xs"
                    style={{ color: "#5A6B85", fontWeight: 500 }}
                  >
                    {item.progressLabel}
                  </p>
                  <div
                    className="mt-2 h-1.5 rounded-full overflow-hidden"
                    style={{ background: "rgba(11,26,51,0.08)" }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${item.progressPercent}%`,
                        background:
                          "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
